// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Public Volunteer Signup
// Public-facing page for volunteer registration & event sign-up
// Route: /volunteer/signup  or  /volunteer/signup/:incidentId
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, TextInput, Select, Button,
  Group, Stack, SimpleGrid, Badge, Checkbox, ScrollArea,
  Stepper, Loader, Center, Divider,
} from '@mantine/core';
import {
  IconHeartHandshake, IconUser, IconPhone, IconMail, IconMapPin,
  IconShieldCheck, IconCalendarEvent, IconCheck, IconChevronLeft,
  IconChevronRight, IconSend,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { fetchTable, createRecord, TABLES, FIELDS } from '../../lib/api-switch';
import { secondary } from '../../lib/colors';

// ── Constants ────────────────────────────────────────────────

const CERT_OPTIONS = [
  'CPR/AED', 'First Aid', 'CERT', 'ICS 100', 'ICS 200', 'ICS 300',
  'ICS 700', 'Ham Radio', 'Chainsaw', 'Swift Water', 'Wilderness FA',
];

const SKILL_OPTIONS = [
  'Medical', 'Communications', 'Logistics', 'Search & Rescue',
  'Heavy Equipment', 'Counseling', 'Translation', 'Cooking',
  'Construction', 'IT/Tech',
];

const EQUIPMENT_OPTIONS = [
  'Chainsaw', 'Generator', 'Water Pump', 'ATV', 'Boat',
  'Trailer', 'Ham Radio', 'Medical Bag',
];

const ROLE_OPTIONS = [
  { value: 'active', label: 'Field Operations' },
  { value: 'office', label: 'Admin / Office' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Communications' },
  { value: 'both', label: 'Flexible / Any' },
];

const VEHICLE_OPTIONS = [
  { value: 'no', label: 'No Vehicle' },
  { value: 'personal', label: 'Personal Vehicle' },
  { value: 'transport', label: 'Can Transport Others' },
];

const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PERIODS = [
  { key: 'morning', label: 'Morning (6a-12p)' },
  { key: 'afternoon', label: 'Afternoon (12-6p)' },
  { key: 'evening', label: 'Evening (6p-12a)' },
  { key: 'overnight', label: 'Overnight (12-6a)' },
];

const WAIVER_TEXT = `VOLUNTEER LIABILITY WAIVER AND RELEASE OF LIABILITY

1. ASSUMPTION OF RISK: I understand that volunteering in emergency management and disaster response activities involves inherent risks including but not limited to physical injury, illness, exposure to hazardous conditions, property damage, disability, or death.

2. RELEASE AND WAIVER OF LIABILITY: I hereby release, waive, discharge, and covenant not to sue the organization, its officers, directors, employees, agents, volunteers, sponsors, and affiliates from any and all liability for any loss, damage, injury, or expense that I may suffer as a result of my participation in volunteer activities.

3. INDEMNIFICATION: I agree to indemnify and hold harmless the organization from any claims, damages, or expenses arising from my participation in volunteer activities.

4. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize the organization to obtain emergency medical treatment on my behalf. I understand that the organization does not provide health insurance coverage for volunteers.

5. PHOTO AND MEDIA RELEASE: I grant the organization permission to use my name, likeness, image, and voice in any and all media for promotional, educational, and informational purposes.

6. GOVERNING LAW: This agreement shall be governed by and construed in accordance with the laws of the state in which volunteer activities are conducted.

7. ACKNOWLEDGEMENT: I have read this Volunteer Liability Waiver and Release of Liability, fully understand its terms, and understand that I am giving up substantial rights by signing it. I acknowledge that I am signing this agreement freely and voluntarily.`;

// ── Types ────────────────────────────────────────────────────

interface PublicEvent {
  id: number;
  incidentId: string;
  name: string;
  type: string;
  status: string;
  location: string;
  address: string;
  startDate: string;
  endDate: string;
  targetVol: number;
  checkInCode: string;
  notes: string;
}

interface PublicIncident {
  id: number;
  name: string;
  status: string;
  incidentType: string;
}

const TYPE_COLORS: Record<string, string> = {
  distribution: 'cyan', mucking: 'orange', medical: 'red',
  intake: 'teal', command: 'grape', shelter: 'yellow', other: 'gray',
};

// ── Component ────────────────────────────────────────────────

export default function PublicSignup() {
  const { incidentId } = useParams<{ incidentId?: string }>();

  // Data
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [incidents, setIncidents] = useState<PublicIncident[]>([]);
  const [loading, setLoading] = useState(true);

  // Registration flow
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [step, setStep] = useState(0); // 0=browse, 1-5=registration steps
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form: Step 1 - Personal
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [dob, setDob] = useState('');
  const [shirtSize, setShirtSize] = useState<string | null>(null);
  const [dietary, setDietary] = useState('');

  // Form: Step 2 - Emergency
  const [ecName, setEcName] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [ecRelationship, setEcRelationship] = useState('');

  // Form: Step 3 - Skills
  const [certs, setCerts] = useState<string[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [rolePref, setRolePref] = useState<string | null>('both');
  const [vehicle, setVehicle] = useState<string | null>('no');
  const [languages, setLanguages] = useState('');

  // Form: Step 4 - Availability
  const [availability, setAvailability] = useState<Record<string, Record<string, boolean>>>({});

  // Form: Step 5 - Waiver
  const [waiverAccepted, setWaiverAccepted] = useState(false);

  // ── Load public data ──────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [evtData, incData] = await Promise.all([
          fetchTable(TABLES.volEvents),
          fetchTable(TABLES.incidents),
        ]);

        const incs: PublicIncident[] = (incData as Record<string, unknown>[]).map(r => ({
          id: Number(r.id) || 0,
          name: (r.name as string) || '',
          status: (r.status as string) || '',
          incidentType: (r.incidentType as string) || '',
        })).filter(i => i.status === 'active' || i.status === 'monitoring');

        const activeIncIds = new Set(incs.map(i => String(i.id)));

        const evts: PublicEvent[] = (evtData as Record<string, unknown>[]).map(r => ({
          id: Number(r.id) || 0,
          incidentId: (r.incidentId as string) || '',
          name: (r.name as string) || '',
          type: (r.type as string) || 'other',
          status: (r.status as string) || '',
          location: (r.location as string) || '',
          address: (r.address as string) || '',
          startDate: (r.startDate as string) || '',
          endDate: (r.endDate as string) || '',
          targetVol: Number(r.targetVol) || 0,
          checkInCode: (r.checkInCode as string) || '',
          notes: (r.notes as string) || '',
        })).filter(e =>
          (e.status === 'planned' || e.status === 'active') &&
          activeIncIds.has(e.incidentId)
        );

        setIncidents(incs);
        setEvents(evts);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtered events ───────────────────────────────────────

  const filteredEvents = useMemo(() => {
    if (incidentId) return events.filter(e => e.incidentId === incidentId);
    return events;
  }, [events, incidentId]);

  const eventsByIncident = useMemo(() => {
    const map = new Map<string, PublicEvent[]>();
    filteredEvents.forEach(e => {
      const arr = map.get(e.incidentId) || [];
      arr.push(e);
      map.set(e.incidentId, arr);
    });
    return map;
  }, [filteredEvents]);

  const selectedEvent = useMemo(() =>
    events.find(e => e.id === selectedEventId) || null,
    [events, selectedEventId],
  );

  // ── Availability toggle ───────────────────────────────────

  const toggleAvail = (day: string, period: string) => {
    setAvailability(prev => {
      const d = day.toLowerCase();
      const dayObj = prev[d] || {};
      return { ...prev, [d]: { ...dayObj, [period]: !dayObj[period] } };
    });
  };

  const isAvail = (day: string, period: string) =>
    !!(availability[day.toLowerCase()] || {})[period];

  // ── Submit ────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      notifications.show({ message: 'Name and email are required', color: 'red' });
      return;
    }
    if (!waiverAccepted) {
      notifications.show({ message: 'You must accept the waiver to continue', color: 'red' });
      return;
    }

    setSubmitting(true);
    try {
      const F = FIELDS.volunteers;
      const extras = {
        dob, address, city, state, zip, shirtSize, dietary,
        ecRelationship,
        equipment,
        languages,
        vehicle,
        waiverAccepted: true,
        waiverDate: new Date().toISOString(),
        selfRegistered: true,
      };

      // Create volunteer (POST first 10 fields, PUT rest)
      const fields: Array<[string, string]> = [
        [F.firstName, firstName.trim()],
        [F.lastName, lastName.trim()],
        [F.email, email.trim()],
        [F.phone, phone.trim()],
        [F.org, ''],
        [F.rolePref, rolePref || 'both'],
        [F.certs, certs.join(', ')],
        [F.skills, skills.join(', ')],
        [F.status, 'probationary'],
        [F.availability, JSON.stringify(availability)],
      ];

      const rid = await createRecord(TABLES.volunteers, fields);

      // PUT remaining fields
      const extraFields: Array<[string, string]> = [
        [F.ecName, ecName],
        [F.ecPhone, ecPhone],
        [F.notes, JSON.stringify(extras)],
        [F.registeredAt, new Date().toISOString()],
      ];

      const { updateRecord } = await import('../../lib/api-switch');
      await updateRecord(TABLES.volunteers, rid, extraFields);

      // If an event was selected, create assignment
      if (selectedEventId && selectedEvent) {
        const AF = FIELDS.volAssignments;
        const asnFields: Array<[string, string]> = [
          [AF.volunteerId, String(rid)],
          [AF.eventId, String(selectedEventId)],
          [AF.incidentId, selectedEvent.incidentId],
          [AF.role, rolePref || 'both'],
          [AF.status, 'confirmed'],
          [AF.shiftDate, selectedEvent.startDate || ''],
          [AF.startTime, ''],
          [AF.endTime, ''],
          [AF.checkIn, ''],
          [AF.checkOut, ''],
        ];
        const asnRid = await createRecord(TABLES.volAssignments, asnFields);
        const asnExtra: Array<[string, string]> = [
          [AF.hours, ''],
          [AF.notes, JSON.stringify({
            volunteerName: `${firstName} ${lastName}`,
            eventName: selectedEvent.name,
            selfRegistered: true,
          })],
        ];
        await updateRecord(TABLES.volAssignments, asnRid, asnExtra);
      }

      setSubmitted(true);
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Registration failed',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    firstName, lastName, email, phone, dob, address, city, state, zip,
    shirtSize, dietary, ecName, ecPhone, ecRelationship, certs, skills,
    equipment, rolePref, vehicle, languages, availability, waiverAccepted,
    selectedEventId, selectedEvent,
  ]);

  // ── Render: Loading ───────────────────────────────────────

  if (loading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" color="teal" />
          <Text c="dimmed">Loading events...</Text>
        </Stack>
      </Center>
    );
  }

  // ── Render: Success ───────────────────────────────────────

  if (submitted) {
    return (
      <Box maw={600} mx="auto" py={60} px="md">
        <Paper p="xl" radius="md" withBorder ta="center">
          <IconCheck size={48} style={{ color: '#059669', marginBottom: 16 }} />
          <Title order={3} mb="sm">Registration Complete!</Title>
          <Text c="dimmed" mb="md">
            Thank you, {firstName}! You have been registered as a volunteer.
            {selectedEvent && (
              <> You are signed up for <b>{selectedEvent.name}</b>.</>
            )}
          </Text>
          <Text size="sm" c="dimmed">
            Your status is set to &quot;probationary&quot; and an administrator will review your registration.
            You will receive further instructions via email at <b>{email}</b>.
          </Text>
        </Paper>
      </Box>
    );
  }

  // ── Render: Event Browser (step 0) ────────────────────────

  if (step === 0) {
    return (
      <Box maw={900} mx="auto" py={40} px="md">
        {/* Hero */}
        <Paper
          p="xl"
          radius="md"
          mb="xl"
          style={{
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: '#fff',
          }}
        >
          <Group gap="md">
            <IconHeartHandshake size={40} />
            <div>
              <Title order={2}>VolunteerOS</Title>
              <Text size="lg" mt={4}>Sign Up to Volunteer</Text>
            </div>
          </Group>
          <Text mt="md" style={{ opacity: 0.9 }}>
            Browse available volunteer opportunities and register to help your community.
          </Text>
        </Paper>

        {filteredEvents.length === 0 ? (
          <Paper p="xl" radius="sm" withBorder ta="center">
            <Text c="dimmed">No volunteer events available at this time. Please check back later.</Text>
          </Paper>
        ) : (
          <Stack gap="lg">
            {Array.from(eventsByIncident.entries()).map(([incId, evts]) => {
              const inc = incidents.find(i => String(i.id) === incId);
              return (
                <div key={incId}>
                  <Group gap="xs" mb="sm">
                    <Text fw={700} size="lg">{inc?.name || 'Incident'}</Text>
                    <Badge variant="light" color="green" tt="capitalize">{inc?.incidentType || ''}</Badge>
                  </Group>
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    {evts.map(ev => (
                      <Paper key={ev.id} p="md" radius="sm" withBorder>
                        <Group justify="space-between" mb="xs">
                          <Text fw={600}>{ev.name}</Text>
                          <Badge size="xs" color={TYPE_COLORS[ev.type] || 'gray'} tt="capitalize">
                            {ev.type}
                          </Badge>
                        </Group>
                        {ev.location && (
                          <Group gap={4} mb={4}>
                            <IconMapPin size={12} style={{ color: secondary[700] }} />
                            <Text size="xs" c="dimmed">{ev.location}</Text>
                          </Group>
                        )}
                        {ev.address && (
                          <Text size="xs" c="dimmed" mb={4}>{ev.address}</Text>
                        )}
                        <Text size="xs" c="dimmed" mb="sm">
                          {ev.startDate || '?'} — {ev.endDate || '?'}
                        </Text>
                        <Button
                          fullWidth
                          color="teal"
                          size="sm"
                          leftSection={<IconCalendarEvent size={16} />}
                          onClick={() => { setSelectedEventId(ev.id); setStep(1); }}
                        >
                          Sign Up for This Event
                        </Button>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </div>
              );
            })}

            <Divider />
            <Button
              variant="light"
              color="teal"
              size="md"
              onClick={() => { setSelectedEventId(null); setStep(1); }}
            >
              Register Without Selecting an Event
            </Button>
          </Stack>
        )}
      </Box>
    );
  }

  // ── Render: Registration Steps (1-5) ──────────────────────

  return (
    <Box maw={700} mx="auto" py={40} px="md">
      <Group gap="xs" mb="lg">
        <IconHeartHandshake size={24} style={{ color: '#059669' }} />
        <Title order={3}>Volunteer Registration</Title>
      </Group>

      {selectedEvent && (
        <Paper p="sm" radius="sm" withBorder mb="md" style={{ borderColor: '#059669' }}>
          <Group gap="xs">
            <IconCalendarEvent size={14} style={{ color: '#059669' }} />
            <Text size="sm" fw={500}>Signing up for: {selectedEvent.name}</Text>
          </Group>
        </Paper>
      )}

      <Stepper active={step - 1} size="sm" mb="xl" color="teal">
        <Stepper.Step label="Personal" />
        <Stepper.Step label="Emergency" />
        <Stepper.Step label="Skills" />
        <Stepper.Step label="Availability" />
        <Stepper.Step label="Waiver" />
      </Stepper>

      {/* Step 1: Personal Info */}
      {step === 1 && (
        <Paper p="md" radius="sm" withBorder>
          <Stack gap="sm">
            <Text fw={600}>Personal Information</Text>
            <SimpleGrid cols={2}>
              <TextInput label="First Name" required value={firstName}
                onChange={e => setFirstName(e.currentTarget.value)}
                leftSection={<IconUser size={14} />} />
              <TextInput label="Last Name" required value={lastName}
                onChange={e => setLastName(e.currentTarget.value)} />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <TextInput label="Email" required value={email}
                onChange={e => setEmail(e.currentTarget.value)}
                leftSection={<IconMail size={14} />} />
              <TextInput label="Phone" value={phone}
                onChange={e => setPhone(e.currentTarget.value)}
                leftSection={<IconPhone size={14} />} />
            </SimpleGrid>
            <TextInput label="Street Address" value={address}
              onChange={e => setAddress(e.currentTarget.value)}
              leftSection={<IconMapPin size={14} />} />
            <SimpleGrid cols={3}>
              <TextInput label="City" value={city}
                onChange={e => setCity(e.currentTarget.value)} />
              <TextInput label="State" value={state}
                onChange={e => setState(e.currentTarget.value)} />
              <TextInput label="ZIP" value={zip}
                onChange={e => setZip(e.currentTarget.value)} />
            </SimpleGrid>
            <SimpleGrid cols={3}>
              <TextInput label="Date of Birth" type="date" value={dob}
                onChange={e => setDob(e.currentTarget.value)} />
              <Select label="T-Shirt Size" data={SHIRT_SIZES} value={shirtSize}
                onChange={setShirtSize} clearable />
              <TextInput label="Dietary Needs" placeholder="Allergies, restrictions..."
                value={dietary} onChange={e => setDietary(e.currentTarget.value)} />
            </SimpleGrid>
          </Stack>
        </Paper>
      )}

      {/* Step 2: Emergency Contact */}
      {step === 2 && (
        <Paper p="md" radius="sm" withBorder>
          <Stack gap="sm">
            <Text fw={600}>Emergency Contact</Text>
            <TextInput label="Contact Name" value={ecName}
              onChange={e => setEcName(e.currentTarget.value)} />
            <SimpleGrid cols={2}>
              <TextInput label="Phone" value={ecPhone}
                onChange={e => setEcPhone(e.currentTarget.value)}
                leftSection={<IconPhone size={14} />} />
              <TextInput label="Relationship" value={ecRelationship}
                onChange={e => setEcRelationship(e.currentTarget.value)} />
            </SimpleGrid>
          </Stack>
        </Paper>
      )}

      {/* Step 3: Skills */}
      {step === 3 && (
        <Paper p="md" radius="sm" withBorder>
          <Stack gap="md">
            <div>
              <Text fw={600} mb="xs">Certifications</Text>
              <Group gap="xs" wrap="wrap">
                {CERT_OPTIONS.map(c => (
                  <Checkbox key={c} label={c} size="xs"
                    checked={certs.includes(c)}
                    onChange={e => {
                      if (e.currentTarget.checked) setCerts(p => [...p, c]);
                      else setCerts(p => p.filter(x => x !== c));
                    }} />
                ))}
              </Group>
            </div>
            <div>
              <Text fw={600} mb="xs">Skills</Text>
              <Group gap="xs" wrap="wrap">
                {SKILL_OPTIONS.map(s => (
                  <Checkbox key={s} label={s} size="xs"
                    checked={skills.includes(s)}
                    onChange={e => {
                      if (e.currentTarget.checked) setSkills(p => [...p, s]);
                      else setSkills(p => p.filter(x => x !== s));
                    }} />
                ))}
              </Group>
            </div>
            <div>
              <Text fw={600} mb="xs">Equipment You Can Bring</Text>
              <Group gap="xs" wrap="wrap">
                {EQUIPMENT_OPTIONS.map(eq => (
                  <Checkbox key={eq} label={eq} size="xs"
                    checked={equipment.includes(eq)}
                    onChange={e => {
                      if (e.currentTarget.checked) setEquipment(p => [...p, eq]);
                      else setEquipment(p => p.filter(x => x !== eq));
                    }} />
                ))}
              </Group>
            </div>
            <SimpleGrid cols={2}>
              <Select label="Role Preference" data={ROLE_OPTIONS} value={rolePref}
                onChange={setRolePref} />
              <Select label="Vehicle" data={VEHICLE_OPTIONS} value={vehicle}
                onChange={setVehicle} />
            </SimpleGrid>
            <TextInput label="Languages Spoken" placeholder="English, Spanish..."
              value={languages} onChange={e => setLanguages(e.currentTarget.value)} />
          </Stack>
        </Paper>
      )}

      {/* Step 4: Availability */}
      {step === 4 && (
        <Paper p="md" radius="sm" withBorder>
          <Stack gap="sm">
            <Text fw={600}>Availability</Text>
            <Text size="xs" c="dimmed">Click cells to mark when you are available to volunteer.</Text>
            <Paper p="xs" radius="sm" withBorder>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: 6, width: 60 }} />
                    {PERIODS.map(p => (
                      <th key={p.key} style={{ padding: 6, textAlign: 'center', fontSize: 11 }}>{p.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map(day => (
                    <tr key={day}>
                      <td style={{ padding: 6, fontWeight: 600 }}>{day}</td>
                      {PERIODS.map(p => (
                        <td
                          key={p.key}
                          style={{
                            padding: 8,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: isAvail(day, p.key) ? '#059669' : 'transparent',
                            color: isAvail(day, p.key) ? '#fff' : 'inherit',
                            border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 4,
                          }}
                          onClick={() => toggleAvail(day, p.key)}
                        >
                          {isAvail(day, p.key) ? 'Yes' : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Paper>
          </Stack>
        </Paper>
      )}

      {/* Step 5: Waiver */}
      {step === 5 && (
        <Paper p="md" radius="sm" withBorder>
          <Stack gap="sm">
            <Text fw={600}>
              <IconShieldCheck size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Volunteer Liability Waiver
            </Text>
            <ScrollArea h={250} type="always">
              <Paper p="md" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{WAIVER_TEXT}</Text>
              </Paper>
            </ScrollArea>
            <Checkbox
              label="I have read and agree to the Volunteer Liability Waiver & Release"
              checked={waiverAccepted}
              onChange={e => setWaiverAccepted(e.currentTarget.checked)}
              color="teal"
            />
          </Stack>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify="space-between" mt="lg">
        <Button
          variant="default"
          leftSection={<IconChevronLeft size={16} />}
          onClick={() => setStep(s => Math.max(0, s - 1))}
        >
          {step === 1 ? 'Back to Events' : 'Previous'}
        </Button>

        {step < 5 ? (
          <Button
            color="teal"
            rightSection={<IconChevronRight size={16} />}
            onClick={() => {
              if (step === 1 && (!firstName.trim() || !lastName.trim() || !email.trim())) {
                notifications.show({ message: 'First name, last name, and email are required', color: 'red' });
                return;
              }
              setStep(s => s + 1);
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            color="teal"
            leftSection={<IconSend size={16} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!waiverAccepted}
          >
            Submit Registration
          </Button>
        )}
      </Group>
    </Box>
  );
}
